import { ReactElement } from 'react';
import { TEST_IDS, DEFAULT_LAMBDA_ROUTES } from '@localstack/constants';
import { useParams } from 'react-router-dom';
import { Alert, Card, CardActions, CardContent, Stack } from '@mui/material';
import { useAwsGetter, useRoutes, getLatestWebRevision } from '@localstack/services';
import { CreateWebFunctionRevisionRequest, LambdaWebRevisionFormValues } from '@localstack/types';
import { LambdaWebRevisionCreateForm, LoadingFragment, PageTitle, ProgressButton } from '@localstack/ui';

import { LambdaProps } from './types';
import { LambdaWebVersionGate } from './components/LambdaWebVersionGate';
import { useWebRevisionCreate } from './hooks/useWebRevisionCreate';
import { WebFunctionTabs } from './components/WebFunctionTabs';

const FORM_ID = 'CreateLambdaWebRevision';
export const LambdaWebRevisionCreate = (props: LambdaProps): ReactElement => (
  <LambdaWebVersionGate {...props}>
    <LambdaWebRevisionCreateContent {...props} />
  </LambdaWebVersionGate>
);

const LambdaWebRevisionCreateContent = ({
  Layout,
  routes = DEFAULT_LAMBDA_ROUTES,
  clientOverrides,
}: LambdaProps): ReactElement => {
  const { name = '' } = useParams<'name'>();
  const { goto } = useRoutes();
  const { data: revisions, isLoading: loadingRevisions } = useAwsGetter(
    'LambdaWeb',
    'listWebFunctionRevisions',
    [{ functionName: name }],
    { clientOverrides, pagination: { inputToken: 'nextToken', outputToken: 'nextToken', itemsKey: 'revisions' } },
  );
  const revisionId = getLatestWebRevision(revisions?.revisions)?.revisionId;
  const { data: revision, isLoading: loadingRevision } = useAwsGetter(
    'LambdaWeb',
    'getWebFunctionRevision',
    [{ functionName: name, revisionId: revisionId ?? '' }],
    { clientOverrides, enable: !!revisionId },
  );
  const { create, isLoading } = useWebRevisionCreate(clientOverrides);
  const loading = loadingRevisions || (!!revisionId && loadingRevision);
  const ready =
    !!revision?.buildConfig?.codeConfig?.s3Object?.bucket &&
    !!revision?.buildConfig?.codeConfig?.s3Object?.key &&
    !!revision?.serviceConfig?.executionRoleArn;
  const handleSubmit = async (
    data: Partial<CreateWebFunctionRevisionRequest>,
    options: Pick<LambdaWebRevisionFormValues, 'codeSource' | 'zipFile'>,
  ) => {
    const source = data.buildConfig?.codeConfig?.s3Object;
    if (
      revision &&
      (await create(name, revision, {
        ...options,
        description: data.description,
        sourceBucket: source?.bucket,
        sourceKey: source?.key,
        sourceVersion: source?.versionId,
      }))
    ) {
      goto(routes.RESOURCES_LAMBDA_WEB_REVISIONS, { name });
    }
  };
  return (
    <Layout
      documentTitle="Lambda: Create Web Function Revision"
      tabs={<WebFunctionTabs routes={routes} functionName={name} clientOverrides={clientOverrides} />}
      title={
        <PageTitle
          title="Create Revision"
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Web Functions', () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTIONS)],
            [name, () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name })],
            ['Revisions', () => goto(routes.RESOURCES_LAMBDA_WEB_REVISIONS, { name })],
            ['Create Revision', null],
          ]}
        />
      }
      actions={
        <ProgressButton
          form={FORM_ID}
          type="submit"
          color="primary"
          variant="outlined"
          loading={isLoading}
          disabled={loading || !ready}
        >
          Submit
        </ProgressButton>
      }
    >
      <LoadingFragment loading={loading}>
        {ready ? (
          <Stack spacing={3}>
            <Alert severity="info">
              Upload a new ZIP or select code from S3 to create an immutable revision. Runtime, execution role, and
              configuration are copied from revision {revisionId}. Endpoints using latest-revision auto deployment will
              receive this revision automatically. The execution role must have permission to read the selected S3
              object.
            </Alert>
            <Card>
              <CardContent>
                <LambdaWebRevisionCreateForm
                  formId={FORM_ID}
                  loading={isLoading}
                  onSubmit={handleSubmit}
                  clientOverrides={clientOverrides}
                />
              </CardContent>
              <CardActions>
                <ProgressButton
                  data-testid={TEST_IDS.LAMBDA_WEB_FORM_SUBMIT}
                  form={FORM_ID}
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isLoading}
                >
                  Submit
                </ProgressButton>
              </CardActions>
            </Card>
          </Stack>
        ) : (
          <Alert severity="info">
            An existing revision with S3 code and an execution role is required to create a revision.
          </Alert>
        )}
      </LoadingFragment>
    </Layout>
  );
};
